import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminUser } from '@/api/admin';
import { CasinoButton } from '@/components/CasinoButton';
import { cn } from '@/lib/utils';
import { AdminHeader } from './TournamentNew';

export function AdminUsers() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'users'], queryFn: adminApi.listUsers });

  const setApproval = useMutation({
    mutationFn: ({ id, isApproved }: { id: number; isApproved: boolean }) =>
      adminApi.setApproval(id, isApproved),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
        qc.invalidateQueries({ queryKey: ['admin', 'pending-summary'] }),
      ]);
    },
  });

  const users = q.data?.users ?? [];
  const pending = users.filter((u) => !u.isApproved && !u.isAdmin);
  const approved = users.filter((u) => u.isApproved || u.isAdmin);

  return (
    <main className="mx-auto max-w-2xl px-5 py-8 space-y-6">
      <AdminHeader
        title="Users"
        crumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Users' }]}
      />

      <section className="cabinet-card p-5 space-y-3">
        <h3 className="font-display text-cabinet-flame-orange text-lg uppercase tracking-widest">
          Pending approval ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <p className="text-sm text-cabinet-cream/60">No one's waiting.</p>
        ) : (
          <ul className="space-y-2">
            {pending.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                actions={
                  <CasinoButton
                    onClick={() => setApproval.mutate({ id: u.id, isApproved: true })}
                    disabled={setApproval.isPending}
                  >
                    Approve
                  </CasinoButton>
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section className="cabinet-card p-5 space-y-3">
        <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
          Approved + admins ({approved.length})
        </h3>
        <ul className="space-y-2">
          {approved.map((u) => (
            <UserRow
              key={u.id}
              u={u}
              actions={
                !u.isAdmin && (
                  <button
                    type="button"
                    onClick={() => setApproval.mutate({ id: u.id, isApproved: false })}
                    className="text-xs text-cabinet-cream/60 hover:text-cabinet-flame-orange"
                    disabled={setApproval.isPending}
                  >
                    Revoke
                  </button>
                )
              }
            />
          ))}
        </ul>
      </section>
    </main>
  );
}

function UserRow({
  u,
  actions,
}: {
  u: AdminUser;
  actions: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm truncate',
            u.isAdmin ? 'text-cabinet-gold' : 'text-cabinet-cream',
          )}
        >
          {u.displayName || u.email}
          {u.isAdmin && (
            <span className="ml-2 text-[10px] uppercase tracking-widest text-cabinet-gold-bright">
              Admin
            </span>
          )}
        </p>
        <p className="text-[10px] text-cabinet-cream/50 truncate">{u.email}</p>
      </div>
      <div>{actions}</div>
    </li>
  );
}
